'use client';

import * as React from 'react';
import { Lightbulb, Bug, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import { cn } from '@/lib/utils';

type FeedbackType = 'feature' | 'bug';

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function collectSessionContext(): Record<string, unknown> {
  try {
    const state = useReceiptBuilder.getState();
    const { template } = state;
    const headerSection = template.sections.find((s) => s.type === 'header');
    const sections = template.sections.map((s) => ({
      type: s.type,
      enabled: s.enabled,
      ...(s.type === 'header' && s.config
        ? {
            businessName: s.config.businessName,
            addressLines: s.config.addressLines,
            logoUrl: s.config.logoUrl || undefined,
          }
        : {}),
      ...(s.type === 'items-list' && s.config
        ? { itemCount: s.config.items?.length }
        : {}),
      ...(s.type === 'totals' && s.config
        ? { lineCount: s.config.lines?.length }
        : {}),
    }));

    return {
      url: typeof window !== 'undefined' ? window.location.href : '',
      templateId: template.id,
      templateName: template.name,
      headerBusiness: headerSection?.type === 'header' ? headerSection.config.businessName : '',
      sectionCount: template.sections.length,
      sections,
      settings: template.settings,
    };
  } catch {
    return { url: typeof window !== 'undefined' ? window.location.href : '' };
  }
}

export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const [type, setType] = React.useState<FeedbackType>('feature');
  const [message, setMessage] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);

  const charCount = message.trim().length;
  const isValid = charCount >= 10 && charCount <= 500;

  const resetForm = React.useCallback(() => {
    setType('feature');
    setMessage('');
    setEmail('');
    setError('');
    setSuccess(false);
    setIsSubmitting(false);
  }, []);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        // Reset after close animation
        setTimeout(resetForm, 200);
      }
      onOpenChange(open);
    },
    [onOpenChange, resetForm]
  );

  const handleSubmit = React.useCallback(async () => {
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          message: message.trim(),
          email: email.trim() || undefined,
          sessionContext: collectSessionContext(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        setIsSubmitting(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        handleOpenChange(false);
      }, 1500);
    } catch {
      setError('Network error. Please try again.');
      setIsSubmitting(false);
    }
  }, [type, message, email, isValid, isSubmitting, handleOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-sm:inset-0 max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-none max-sm:w-full max-sm:h-full max-sm:max-h-none max-sm:rounded-none max-sm:border-0 overflow-y-auto z-[1100]">
        {success ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="text-lg font-medium">Thank you!</p>
            <p className="text-sm text-muted-foreground">
              Your feedback has been sent.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Send Feedback</DialogTitle>
              <DialogDescription>
                Help us improve Receipt Maker. Suggest a feature or report a
                bug.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              {/* Type selector */}
              <div className="space-y-2">
                <Label className="text-sm">What kind of feedback?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setType('feature')}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border p-3 text-sm transition-colors',
                      type === 'feature'
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted'
                    )}
                  >
                    <Lightbulb className="h-4 w-4 shrink-0" />
                    <span>Feature request</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('bug')}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border p-3 text-sm transition-colors',
                      type === 'bug'
                        ? 'border-destructive bg-destructive/5 text-foreground'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted'
                    )}
                  >
                    <Bug className="h-4 w-4 shrink-0" />
                    <span>Bug report</span>
                  </button>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="feedback-message" className="text-sm">
                    Message
                  </Label>
                  <span
                    className={cn(
                      'text-xs',
                      charCount > 500
                        ? 'text-destructive'
                        : charCount >= 10
                          ? 'text-muted-foreground'
                          : 'text-muted-foreground/60'
                    )}
                  >
                    {charCount}/500
                  </span>
                </div>
                <Textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    type === 'feature'
                      ? 'Describe the feature you would like...'
                      : 'Describe the bug or issue...'
                  }
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Email (optional) */}
              <div className="space-y-2">
                <Label htmlFor="feedback-email" className="text-sm">
                  Email{' '}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </Label>
                <Input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  Only used if we need to follow up.
                </p>
              </div>

              {/* Error message */}
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Send Feedback
                  </span>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
